import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import UIButton from "../UIButton";
import UIInput from "../UIInput";
import UILabel from "../UILabel";
import { statusOptions } from "../../hooks/useProjectsData";

export default function ProjectFormModal({
  open,
  onClose,
  onSave,
  form,
  setForm,
  clients,
  editingId,
  text,
  theme,
}) {
  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const shellClass =
    theme === "dark"
      ? "border-slate-800 bg-slate-950 text-slate-100"
      : "border-slate-200 bg-white text-slate-900";

  return createPortal(
    <div className="fixed inset-0 z-[120] overflow-y-auto bg-slate-950/45 px-4 py-6 backdrop-blur-[3px] md:px-6 md:py-10">
      <div className="mx-auto flex min-h-full items-start justify-center md:items-center">
        <div className={`relative flex max-h-[calc(100vh-3rem)] w-full max-w-4xl flex-col overflow-hidden rounded-[30px] border shadow-[0_30px_80px_rgba(15,23,42,0.18)] md:max-h-[calc(100vh-5rem)] ${shellClass}`}>
          <div className={`sticky top-0 z-10 border-b px-4 py-4 md:px-6 ${theme === "dark" ? "border-slate-800 bg-slate-950/95" : "border-slate-200 bg-white/95"}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-xl font-black md:text-2xl">{editingId ? text.editProject : text.newProject}</h3>
                <p className={`mt-1 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{text.subtitle}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className={`flex h-11 w-11 items-center justify-center rounded-2xl border transition ${theme === "dark" ? "border-slate-800 bg-slate-900 hover:bg-slate-800" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <form onSubmit={onSave} className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <UILabel>{text.projectName}</UILabel>
                <UIInput value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder={text.projectName} required />
              </div>

              <div>
                <UILabel>{text.clientLink}</UILabel>
                <select
                  value={form.clientId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedClient = clients.find((c) => c.id === selectedId);
                    setForm((prev) => ({
                      ...prev,
                      clientId: selectedId,
                      clientName: selectedClient?.name || selectedClient?.companyName || prev.clientName || "",
                    }));
                  }}
                  className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                >
                  <option value="">{text.noLinkedClient}</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name || client.companyName || client.contactPerson || "Client"}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <UILabel>{text.clientName}</UILabel>
                <UIInput value={form.clientName} onChange={(e) => setForm((p) => ({ ...p, clientName: e.target.value }))} placeholder={text.clientName} />
              </div>

              <div className="md:col-span-2">
                <UILabel>{text.address}</UILabel>
                <UIInput value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} placeholder={text.address} />
              </div>

              <div>
                <UILabel>{text.status}</UILabel>
                <select
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>{text[status]}</option>
                  ))}
                </select>
              </div>

              <div>
                <UILabel>{text.budget}</UILabel>
                <UIInput type="number" step="0.01" value={form.budget} onChange={(e) => setForm((p) => ({ ...p, budget: e.target.value }))} placeholder="0.00" />
              </div>

              <div>
                <UILabel>{text.startDate}</UILabel>
                <UIInput type="date" value={form.startDate} onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))} />
              </div>

              <div>
                <UILabel>{text.endDate}</UILabel>
                <UIInput type="date" value={form.endDate} onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))} />
              </div>

              <div>
                <UILabel>{text.laborCost}</UILabel>
                <UIInput type="number" step="0.01" value={form.laborCost} onChange={(e) => setForm((p) => ({ ...p, laborCost: e.target.value }))} />
              </div>
              <div>
                <UILabel>{text.materialCost}</UILabel>
                <UIInput type="number" step="0.01" value={form.materialCost} onChange={(e) => setForm((p) => ({ ...p, materialCost: e.target.value }))} />
              </div>
              <div>
                <UILabel>{text.otherCost}</UILabel>
                <UIInput type="number" step="0.01" value={form.otherCost} onChange={(e) => setForm((p) => ({ ...p, otherCost: e.target.value }))} />
              </div>
              <div>
                <UILabel>{text.totalHours}</UILabel>
                <UIInput type="number" step="0.1" value={form.totalHours} onChange={(e) => setForm((p) => ({ ...p, totalHours: e.target.value }))} />
              </div>
              <div>
                <UILabel>{text.workersCount}</UILabel>
                <UIInput type="number" value={form.workersCount} onChange={(e) => setForm((p) => ({ ...p, workersCount: e.target.value }))} />
              </div>
              <div>
                <UILabel>{text.materialsCount}</UILabel>
                <UIInput type="number" value={form.materialsCount} onChange={(e) => setForm((p) => ({ ...p, materialsCount: e.target.value }))} />
              </div>
              <div>
                <UILabel>{text.invoiceCount}</UILabel>
                <UIInput type="number" value={form.invoiceCount} onChange={(e) => setForm((p) => ({ ...p, invoiceCount: e.target.value }))} />
              </div>

              <div className="md:col-span-2">
                <UILabel>{text.notes}</UILabel>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder={text.notesPlaceholder}
                  rows={4}
                  className={`w-full rounded-3xl border px-4 py-3 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500" : "border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400"}`}
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 border-t pt-4 sm:flex-row sm:justify-end">
              <UIButton type="button" variant="secondary" onClick={onClose}>{text.cancel}</UIButton>
              <UIButton type="submit">{editingId ? text.update : text.save}</UIButton>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
